import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import {
  RefreshGridDataAction,
  GridDataRefreshSuccessAction,
  GridDataRefreshFailedAction,
  GridChangeSelections,
  GridClearSelections,
  GridSetSelections,
  GridSetSelectionsData,
  GridSetState,
  GridSetExpandedIds,
  GridSetStateAndRefreshData,
  GridSetFiltersVisible,
} from '../actions/grid.actions';
import { Grid } from '../models/grid.model';
import { createDefaultGridPayload } from '../models/gridPayload';

export type GridsState = EntityState<Grid>;

export const adapter: EntityAdapter<Grid> = createEntityAdapter<Grid>();

export const initialState: GridsState = adapter.getInitialState();

const emptyData: GridDataResult = {
  data: [],
  total: 0,
};

const emptyState: DataSourceRequestState = {};

function getGridOrDefault(state: GridsState, id: string): Grid {
  return (
    state.entities[id] ?? {
      id,
      loading: false,
      data: emptyData,
      state: emptyState,
      errors: null,
      payload: createDefaultGridPayload(),
    }
  );
}

function stripUndefinedSettings(settings?: DataSourceRequestState | null): DataSourceRequestState {
  if (!settings) {
    return emptyState;
  }

  return Object.fromEntries(
    Object.entries(settings).filter(([, value]) => value !== undefined)
  ) as DataSourceRequestState;
}

const gridReducer = createReducer(
  initialState,
  on(RefreshGridDataAction, (state, { id, settings, payload }) => {
    const oldState = getGridOrDefault(state, id);
    const newSettings = stripUndefinedSettings(settings);
    return adapter.upsertOne(
      {
        id,
        loading: true,
        data: oldState.data,
        state: { ...oldState.state, ...newSettings },
        errors: oldState.errors,
        payload: createDefaultGridPayload(payload ? { ...oldState.payload, ...payload } : oldState.payload),
      },
      state
    );
  }),
  // tslint:disable-next-line:no-shadowed-variable
  on(GridDataRefreshSuccessAction, (state, { id, gridResponse, total, gridErrors }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: {
          data: gridResponse || [],
          total,
        },
        state: oldState.state,
        errors: gridErrors,
        payload: oldState.payload,
      },
      state
    );
  }),
  on(GridDataRefreshFailedAction, (state, { id }) => {
    const stateGridBefore = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: {
          data: [],
          total: 0,
        },
        state: stateGridBefore.state,
        errors: {
          errorCode: -1,
          errorDescription: 'Something is wrong, please repeate action.',
          success: false,
        },
        payload: createDefaultGridPayload(stateGridBefore.payload),
      },
      state
    );
  }),
  on(GridChangeSelections, (state, { id /*, addedSelections, removedSelections*/ }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: oldState.payload, //selections: [...oldState.selections.filter(x => removedSelections.findIndex(y => y === x) < 0), ...addedSelections]
      },
      state
    );
  }),
  on(GridClearSelections, (state, { id }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: createDefaultGridPayload({
          showSelected: false,
          selections: [],
          filtersVisible: oldState.payload?.filtersVisible,
        }),
      },
      state
    );
  }),
  on(GridSetSelections, (state, { id, newSelections }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: createDefaultGridPayload({
          ...oldState.payload,
          selections: [...newSelections],
        }),
      },
      state
    );
  }),
  on(GridSetSelectionsData, (state, { id, newSelectionsData }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: createDefaultGridPayload({
          ...oldState.payload,
          selectionsData: newSelectionsData,
        }),
      },
      state
    );
  }),
  on(GridSetState, (state, { id, newState }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: newState,
        errors: oldState.errors,
        payload: createDefaultGridPayload(oldState.payload),
      },
      state
    );
  }),
  on(GridSetStateAndRefreshData, (state, { id, newState }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: { ...oldState.state, ...newState },
        errors: oldState.errors,
        payload: createDefaultGridPayload(oldState.payload),
      },
      state
    );
  }),
  on(GridSetExpandedIds, (state, { id, expandedIds }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: createDefaultGridPayload({
          ...oldState.payload,
          expandedIds: expandedIds,
        }),
      },
      state
    );
  }),
  on(GridSetFiltersVisible, (state, { id, filtersVisible }) => {
    const oldState = getGridOrDefault(state, id);
    return adapter.upsertOne(
      {
        id,
        loading: false,
        data: oldState.data,
        state: oldState.state,
        errors: oldState.errors,
        payload: createDefaultGridPayload({
          ...oldState.payload,
          filtersVisible: filtersVisible,
        }),
      },
      state
    );
  })
);

export function reducer(state: GridsState | undefined, action: Action): GridsState {
  return gridReducer(state, action);
}
